const POINT_AUTH_SUCCESS_CODES = [1, "1", 200, "200"];
const POINT_TOKEN_STORAGE_KEY = "xcoins_point_token";
const POINT_TOKEN_TTL = 7 * 24 * 60 * 60 * 1000;
const POINT_LOGIN_COOLDOWN = 5000;

function isPointAuthSuccess(res) {
  if (!res || res.data === false) {
    return false;
  }

  const data = res.data || {};
  if (data.is_login === false || data.isLogin === false || data.login === false) {
    return false;
  }

  return (
    POINT_AUTH_SUCCESS_CODES.includes(res.code) ||
    POINT_AUTH_SUCCESS_CODES.includes(data.code)
  );
}

function extractPointToken(res) {
  return res?.data?.data?.access_token || "";
}

function normalizePointDetail(data) {
  return data?.detail || data || {};
}

function getRequestUrl(res) {
  return (
    res?.config?.url ||
    res?.response?.config?.url ||
    res?.request?.responseURL ||
    ""
  );
}

function isPointUnauthorized(res) {
  const response = res?.response || {};
  const data = res?.data || response.data || {};
  const status = res?.status || response.status || data.status;
  const code = res?.code || data.code;
  if (String(status) !== "401" && String(code) !== "401") {
    return false;
  }

  const url = String(getRequestUrl(res));
  if (url.includes("/mallPoints/api/goods")) {
    return false;
  }

  return url.includes("/mallPoints/") || url.includes("/point/") || !url;
}

function readPersistedPointToken() {
  if (!process.client) {
    return null;
  }

  try {
    const parsed = JSON.parse(localStorage.getItem(POINT_TOKEN_STORAGE_KEY) || "");
    const token = parsed?.token || "";
    const obtainedAt = Number(parsed?.obtainedAt) || 0;
    if (!token) {
      return null;
    }
    if (obtainedAt && Date.now() - obtainedAt >= POINT_TOKEN_TTL) {
      localStorage.removeItem(POINT_TOKEN_STORAGE_KEY);
      return null;
    }
    return { token, obtainedAt };
  } catch (e) {
    return null;
  }
}

function persistPointToken(token) {
  if (!process.client || !token) {
    return;
  }

  localStorage.setItem(
    POINT_TOKEN_STORAGE_KEY,
    JSON.stringify({
      token,
      obtainedAt: Date.now(),
    })
  );
}

function clearPersistedPointToken() {
  if (!process.client) {
    return;
  }
  localStorage.removeItem(POINT_TOKEN_STORAGE_KEY);
}

export default ({ $global, store }, inject) => {
  let pointTokenPromise = null;
  let pointDetailPromise = null;

  if (process.client && store && !store.state.pointToken) {
    const saved = readPersistedPointToken();
    if (saved?.token) {
      store.commit("SET_POINT_TOKEN", saved.token);
    }
  }

  if (store && typeof store.subscribe === "function") {
    store.subscribe((mutation) => {
      if (mutation.type === "SET_POINT_TOKEN" && mutation.payload) {
        const saved = readPersistedPointToken();
        if (!saved || saved.token !== mutation.payload) {
          persistPointToken(mutation.payload);
        }
        return;
      }
      if (mutation.type === "CLEAR_POINT_CACHE" || mutation.type === "SIGNOUT") {
        clearPersistedPointToken();
      }
    });
  }

  function reuseExistingToken({ force = false } = {}) {
    const memoryToken = store.state.pointToken || "";
    const saved = readPersistedPointToken();
    const token = memoryToken || saved?.token || "";
    if (!token) {
      return "";
    }

    if (saved?.token && saved.token !== memoryToken) {
      store.commit("SET_POINT_TOKEN", saved.token);
    }

    if (!force) {
      return store.state.pointToken || token;
    }

    const obtainedAt = saved?.obtainedAt || 0;
    if (obtainedAt && Date.now() - obtainedAt < POINT_LOGIN_COOLDOWN) {
      return store.state.pointToken || token;
    }

    return "";
  }

  async function ensurePointToken({ force = false } = {}) {
    if (!store || !$global) {
      return "";
    }

    const existingToken = reuseExistingToken({ force });
    if (existingToken) {
      return existingToken;
    }

    if (pointTokenPromise) {
      return pointTokenPromise;
    }

    const previousToken = store.state.pointToken || readPersistedPointToken()?.token || "";

    pointTokenPromise = (async () => {
      try {
        const authRes = await $global.pointAuth();
        if (!isPointAuthSuccess(authRes)) {
          return previousToken;
        }

        const loginRes = await $global.pointLogin();
        const pointToken = extractPointToken(loginRes);
        if (!pointToken) {
          return previousToken;
        }

        store.commit("SET_POINT_TOKEN", pointToken);
        return pointToken;
      } catch (e) {
        return previousToken;
      }
    })();

    try {
      return await pointTokenPromise;
    } finally {
      pointTokenPromise = null;
    }
  }

  async function ensurePointDetail() {
    if (!store || !$global) {
      return {};
    }

    if (pointDetailPromise) {
      return pointDetailPromise;
    }

    pointDetailPromise = (async () => {
      try {
        const detailRes = await requestWithPointToken((pointToken) => {
          return $global.pointsCenterDetail(undefined, pointToken);
        });

        if (detailRes && detailRes.code == 200) {
          const pointDetail = normalizePointDetail(detailRes.data);
          store.commit("SET_POINT_DETAIL", pointDetail);
          return pointDetail;
        }
      } catch (e) {}

      return store.state.pointDetail || {};
    })();

    try {
      return await pointDetailPromise;
    } finally {
      pointDetailPromise = null;
    }
  }

  async function requestWithPointToken(requestFn) {
    let pointToken = await ensurePointToken();
    if (!pointToken) {
      return null;
    }

    try {
      const res = await requestFn(pointToken);
      if (!isPointUnauthorized(res)) {
        return res;
      }
    } catch (e) {
      if (!isPointUnauthorized(e)) {
        throw e;
      }
    }

    const currentToken = store.state.pointToken || readPersistedPointToken()?.token || "";
    if (currentToken && currentToken !== pointToken) {
      return requestFn(currentToken);
    }

    const refreshedToken = await ensurePointToken({ force: true });
    if (!refreshedToken) {
      return null;
    }
    if (refreshedToken === pointToken) {
      return null;
    }

    return requestFn(refreshedToken);
  }

  inject("pointsCache", {
    ensurePointToken,
    ensurePointDetail,
    requestWithPointToken,
    isPointUnauthorized,
  });
};
